import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { jwtDecode } from "jwt-decode";
import { API, API_BASE_URL } from "../config/apiConfig";
import { portal, portalGradientTextSx } from "../styles/dashboardBrand";
import DashboardPageHeader from "../components/DashboardPageHeader";
import { useUtcMidnightCountdown, formatUtcMidnightCountdown } from "../hooks/useUtcMidnightCountdown";
import { dreameyDiamondsUserSum } from "../utils/dreameyDiamonds";
import {
  readHomeCache,
  readUserSessionProfileCache,
  writeHomeCache,
  writeUserSessionProfileCache,
  mergeDashboardRawFields,
  mergeHomeCacheSelfInfo,
  readPersistentHomeRankProgressCache,
  mergePersistentHomeRankProgressCache,
  getCurrentUtcSixHourSlotStartMs,
  shouldForceUserDashboardHomeNetwork,
  clearUserDashboardHomeForceNetwork,
  isHomeCacheComplete,
} from "../utils/userDashboardHomeCache";
import { buildReferralsActivitySnapshot } from "../utils/referralsActivitySnapshot";

/** Same display rules as WelcomeHeader My Summary — 1 DC = 100 Diamonds; 1 Ruby = 5 EP (MTD). */
const DIAMONDS_PER_DREAMEY_COIN = 100;
const ENGAGEMENT_POINTS_PER_RUBY = 5;
/** Many direct refs → heavy parallel fetches; skip idle deferral and full-card loading chrome so the list renders as ready. */
const HOME_MANY_REFERRALS_THRESHOLD = 15;
const DREAMEY_COIN_IMAGE_SRC = `${process.env.PUBLIC_URL || ""}/dreamey-coin-dc.png`;
const RUBY_GEM_IMAGE_SRC = `${process.env.PUBLIC_URL || ""}/dreamey-ruby-gem.png`;

/** Dreamey Hub store URLs — same as `src/user/Apps.js`. */
const DREAMEY_HUB_IOS_URL = "https://apps.apple.com/us/developer/imaginative-solutions/id1855143912";
const DREAMEY_HUB_ANDROID_URL = "https://play.google.com/store/apps/developer?id=Imaginative+Solutions&hl=en";

function isAppleDeviceUserAgent() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod|Macintosh|Mac OS/i.test(ua);
}

function openDreameyHubStore() {
  if (typeof window === "undefined") return;
  const url = isAppleDeviceUserAgent() ? DREAMEY_HUB_IOS_URL : DREAMEY_HUB_ANDROID_URL;
  window.open(url, "_blank", "noopener,noreferrer");
}

const dreameyCoinFlipKeyframesHome = {
  "@keyframes dreameyCoinFlipYHome": {
    "0%": { transform: "rotateY(0deg)" },
    "100%": { transform: "rotateY(360deg)" },
  },
};
const dreameyCoinFlipAnimationSxHome = {
  ...dreameyCoinFlipKeyframesHome,
  animation: "dreameyCoinFlipYHome 2.75s linear infinite",
  transformStyle: "preserve-3d",
  backfaceVisibility: "visible",
};

const loadingSnakeBorderKeyframesHome = {
  "@keyframes loadingSnakeBorderSpinHome": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

const homeProgressShimmerKeyframes = {
  "@keyframes homeProgressShimmer": {
    "0%": { transform: "translateX(-120%)" },
    "100%": { transform: "translateX(220%)" },
  },
};

const homeCtaShineKeyframes = {
  "@keyframes homeCtaShine": {
    "0%": { transform: "translateX(-160%) skewX(-22deg)" },
    "60%": { transform: "translateX(260%) skewX(-22deg)" },
    "100%": { transform: "translateX(260%) skewX(-22deg)" },
  },
};

const homeCtaGradientShiftKeyframes = {
  "@keyframes homeCtaGradientShift": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
};

const homeCtaBounceKeyframes = {
  "@keyframes homeCtaBounce": {
    "0%, 100%": { transform: "scale(1) translateY(0)" },
    "50%": { transform: "scale(1.025) translateY(-2px)" },
  },
};

const homeStreakPulseKeyframes = {
  "@keyframes homeStreakPulse": {
    "0%, 100%": {
      transform: "scale(1)",
      filter: `drop-shadow(0 0 6px ${alpha("#FBBF24", 0.55)})`,
    },
    "50%": {
      transform: "scale(1.12)",
      filter: `drop-shadow(0 0 14px ${alpha("#FBBF24", 0.85)})`,
    },
  },
};

const homeIconFloatKeyframes = {
  "@keyframes homeIconFloat": {
    "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
    "50%": { transform: "translateY(-2px) rotate(-3deg)" },
  },
};

const homeCardEntryKeyframes = {
  "@keyframes homeStatCardPulse": {
    "0%, 100%": {
      boxShadow: `0 0 0 ${alpha("#4FACFE", 0)}`,
    },
    "50%": {
      boxShadow: `0 0 24px ${alpha("#4FACFE", 0.18)}`,
    },
  },
};

function homeCardGradientBorderSx(theme, isLoading = false, gradient = portal.gradientBar, innerBackground = null) {
  const isDark = theme.palette.mode === "dark";
  const cardBackground = isDark
    ? `linear-gradient(165deg, ${alpha("#12182a", 0.96)} 0%, ${alpha("#0b1222", 0.94)} 45%, ${alpha("#080c14", 0.97)} 100%)`
    : `linear-gradient(165deg, ${alpha("#ffffff", 0.99)} 0%, ${alpha("#f5f9ff", 0.96)} 100%)`;
  return {
    ...(isLoading ? loadingSnakeBorderKeyframesHome : {}),
    border: `1px solid ${alpha(isDark ? portal.cyan : theme.palette.common.white, isDark ? 0.16 : 0.35)}`,
    overflow: "hidden",
    "&::before": isLoading
      ? {
          content: '""',
          position: "absolute",
          inset: -90,
          background: `conic-gradient(from 0deg, transparent 0deg, transparent 250deg, ${alpha(portal.cyan, 0.05)} 280deg, ${portal.cyan} 305deg, ${portal.pink} 330deg, transparent 360deg)`,
          animation: "loadingSnakeBorderSpinHome 1.45s linear infinite",
          pointerEvents: "none",
        }
      : {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background: gradient,
          opacity: isDark ? 0.72 : 0.62,
          pointerEvents: "none",
        },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: isLoading ? 2 : 1.5,
      borderRadius: "calc(24px - 2px)",
      background: innerBackground || cardBackground,
      pointerEvents: "none",
    },
    "& > *": {
      position: "relative",
      zIndex: 1,
    },
  };
}

/** Large metric: integer / groups in amber, decimals in cyan (matches reference UI). */
function SplitMetricValue({ value, maxFractionDigits = 2, compact = false }) {
  const n = Number(value);
  const sizeSx = compact
    ? { fontSize: { xs: "1rem", sm: "1.75rem", md: "2.35rem" } }
    : { fontSize: { xs: "1.65rem", sm: "2rem" } };
  if (!Number.isFinite(n)) {
    return (
      <Typography
        component="span"
        sx={{
          fontWeight: 800,
          color: "text.secondary",
          ...sizeSx,
        }}
      >
        —
      </Typography>
    );
  }
  const parts = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0,
  }).formatToParts(n);
  const amber = "#FBBF24";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        flexWrap: "nowrap",
        minWidth: 0,
        justifyContent: "center",
      }}
    >
      {parts.map((part, i) => {
        const isFrac = part.type === "fraction" || part.type === "decimal";
        const color = isFrac ? portal.cyan : amber;
        return (
          <Typography
            key={`${part.type}-${i}`}
            component="span"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.01em",
              color,
              lineHeight: 1.1,
              ...sizeSx,
            }}
          >
            {part.value}
          </Typography>
        );
      })}
    </Box>
  );
}

function designationNameFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  const raw =
    record.DesignationName ??
    record.designationName ??
    record.DESIGNATIONNAME;
  const s = String(raw ?? "").trim();
  if (!s || s.toLowerCase() === "null") return "";
  return s;
}

function normalizeStreakPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  return {
    adsWatchedToday: Number(r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0) || 0,
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 10) || 10,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
  };
}

/** Per-referral streak (same fields as Activity page) for dashboard cache / snapshot. */
function normalizeStreakPayloadForRef(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  const last30 =
    r.last30Days ?? r.Last30Days ?? r.last_30_days ?? [];
  return {
    adsWatchedToday: Number(
      r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0
    ),
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 5) || 5,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
    last30Days: Array.isArray(last30) ? last30 : [],
  };
}

/** True when today's ad goal is met (streak uses UTC day from API). */
function isStreakGoalMetToday(adsWatchedToday, goalPerDay) {
  const g = Math.max(1, Number(goalPerDay) || 10);
  return Number(adsWatchedToday ?? 0) >= g;
}

function displayNameFromRecord(obj, fallback = "Member") {
  const first = String(obj?.FirstName ?? obj?.firstName ?? "").trim();
  const last = String(obj?.LastName ?? obj?.lastName ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  const userName = String(obj?.UserName ?? obj?.userName ?? obj?.username ?? "").trim();
  if (userName) return userName;
  return fallback;
}

/**
 * Rank label from main.Designation (API). Tree "Level" is placement depth only — never use it as a rank title.
 * If Users.DesignationID is NULL, there is no designation row; show Member (or Leader for self when defaultLeader).
 */
/** Lower index = higher tier (Diamond first, Member last). Used only for referral list ordering. */
const REFERRAL_RANK_SORT_ORDER = [
  "Diamond Executive",
  "Gold Executive",
  "Silver Executive",
  "Team Leader",
  "Leader",
  "Member",
];

function referralRankSortIndex(rankLabel) {
  const s = String(rankLabel ?? "")
    .trim()
    .toLowerCase();
  if (!s) return REFERRAL_RANK_SORT_ORDER.length;
  for (let i = 0; i < REFERRAL_RANK_SORT_ORDER.length; i++) {
    if (s === REFERRAL_RANK_SORT_ORDER[i].toLowerCase()) return i;
  }
  if (s.includes("diamond")) return 0;
  if (s.includes("gold") && s.includes("executive")) return 1;
  if (s.includes("silver") && s.includes("executive")) return 2;
  if (s.includes("team") && s.includes("leader")) return 3;
  if (s === "leader") return 4;
  if (s.includes("member")) return 5;
  return REFERRAL_RANK_SORT_ORDER.length;
}

function resolveRankLabel(record, designationByUserId, userId, defaultLeader = false) {
  const fromDb = designationNameFromRecord(record);
  if (fromDb) return fromDb;
  const uid = Number(userId);
  if (Number.isFinite(uid) && designationByUserId && typeof designationByUserId === "object") {
    const fromMap = String(designationByUserId[uid] ?? "").trim();
    if (fromMap && fromMap.toLowerCase() !== "null") return fromMap;
  }
  const fromRankFields = String(
    record?.ReferralRank ??
      record?.referralRank ??
      record?.RankName ??
      record?.rankName ??
      record?.RankTitle ??
      record?.rankTitle ??
      ""
  ).trim();
  if (fromRankFields) return fromRankFields;
  return defaultLeader ? "Leader" : "Member";
}

function getEngagementPointsFromEarnings(earnings) {
  if (!earnings || typeof earnings !== "object") return 0;
  const raw =
    earnings.CurrentMonthTokens ??
    earnings.currentMonthTokens ??
    earnings.TotalEngagementPoints ??
    earnings.EngagementPoints ??
    earnings.TotalTokens ??
    earnings.Tokens ??
    earnings.TotalEarnings ??
    0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

function buildLedgerUrl(userId, fromDate, toDate) {
  let base;
  if (API?.tokenLedger) base = `${API.tokenLedger}/${userId}`;
  else if (API?.tokenLedgerByUser) base = `${API.tokenLedgerByUser}/${userId}`;
  else if (API?.earnings) base = `${API.earnings}/token-ledger/${userId}`;
  else base = `${API_BASE_URL}/earnings/token-ledger/${userId}`;
  if (fromDate && toDate) {
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`;
  }
  return base;
}

function getCurrentMonthDateRange() {
  const now = new Date();
  const fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return {
    from: fromDate.toISOString().split("T")[0],
    to: now.toISOString().split("T")[0],
  };
}

function sumEngagementPointsFromLedgerPayload(payload) {
  const items = Array.isArray(payload) ? payload : (payload?.ledger ?? payload?.data ?? []);
  return Array.isArray(items)
    ? items.reduce((sum, tx) => sum + (Number(tx?.TokensGenerated ?? tx?.Tokens ?? 0) || 0), 0)
    : 0;
}

function getUserIdFromToken() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    const userId = Number(decoded?.userId ?? decoded?.UserID ?? decoded?.id);
    return Number.isFinite(userId) ? userId : null;
  } catch {
    return null;
  }
}

function buildFallbackHomeSelfInfo(userId, raw = null) {
  let decoded = {};
  try {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    decoded = token ? jwtDecode(token) : {};
  } catch {
    decoded = {};
  }
  const sessionProfile = readUserSessionProfileCache(userId);
  const profilePayload = sessionProfile?.profile ?? raw?.profile;
  const profile = profilePayload?.user ?? profilePayload ?? {};
  const earnings = raw?.earnings?.earnings ?? raw?.earnings?.data ?? raw?.earnings ?? {};
  const engagementPoints = raw?.ledger
    ? sumEngagementPointsFromLedgerPayload(raw.ledger)
    : getEngagementPointsFromEarnings(earnings);
  const streak = normalizeStreakPayload(raw?.streak);
  const persistentRankSummary = readPersistentHomeRankProgressCache(userId)?.selfRankSummary;
  const cachedRank = String(raw?.rankSummary?.rank ?? persistentRankSummary?.rank ?? "").trim();
  const refCode = String(
      sessionProfile?.referralCode ??
      profile?.ReferralCode ??
      profile?.referralCode ??
      decoded?.referralCode ??
      decoded?.ReferralCode ??
      ""
  ).trim();
  return {
    name: displayNameFromRecord({ ...decoded, ...profile }, "You"),
    rank: cachedRank || resolveRankLabel(profile, null, userId, false),
    engagementPoints,
    currentStreak: streak?.currentStreak ?? 0,
    levelsBarPercent: Number(raw?.rankSummary?.levelsBarPercent ?? persistentRankSummary?.levelsBarPercent ?? 0) || 0,
    referralsBarPercent: Number(raw?.rankSummary?.referralsBarPercent ?? persistentRankSummary?.referralsBarPercent ?? 0) || 0,
    todayGoalPercent:
      (streak?.goalPerDay ?? 0) > 0
        ? Math.min(100, ((streak?.adsWatchedToday ?? 0) / (streak?.goalPerDay ?? 1)) * 100)
        : 0,
    adsWatchedToday: streak?.adsWatchedToday ?? 0,
    goalPerDay: streak?.goalPerDay ?? 10,
    referralLink: refCode ? `https://dv-apps.com/signup?ref=${encodeURIComponent(refCode)}` : "",
    totalDreameyDiamonds: raw?.coins?.success ? dreameyDiamondsUserSum(raw.coins) : 0,
    activeConnections: Number(earnings?.ActiveConnections ?? earnings?.activeConnections ?? 0) || 0,
  };
}

/** Same tier thresholds as Activity (Leader → Diamond) — network depth & direct refs. */
const RANK_BAR_DEPTH_THRESHOLDS = [3, 6, 9, 12, 15];
const RANK_BAR_REFERRAL_THRESHOLDS = [2, 4, 6, 8, 10];

function rankMetricToBarFillPercent(rawValue, thresholdsAscending) {
  const d = Math.max(0, Number(rawValue) || 0);
  const tiers = thresholdsAscending;
  const n = tiers.length;
  if (n === 0) return 0;
  const segW = 100 / n;
  if (d >= tiers[n - 1]) return 100;
  if (d < tiers[0]) return tiers[0] > 0 ? (d / tiers[0]) * segW : 0;
  for (let k = 0; k < n - 1; k++) {
    if (d >= tiers[k] && d < tiers[k + 1]) {
      const span = tiers[k + 1] - tiers[k];
      const t = span > 0 ? (d - tiers[k]) / span : 0;
      return (k + 1) * segW + t * segW;
    }
  }
  return 100;
}

function rankLevelsToBarFillPercent(level) {
  return rankMetricToBarFillPercent(level, RANK_BAR_DEPTH_THRESHOLDS);
}

function rankReferralsToBarFillPercent(directRefCount) {
  return rankMetricToBarFillPercent(directRefCount, RANK_BAR_REFERRAL_THRESHOLDS);
}

/** Leader → Diamond Executive (same order as Activity `RANK_BAR_SEGMENTS` / Ranks & Diamonds). */
const RANK_LADDER_SEGMENTS = [
  { rank: "Leader", levelMin: 3, directReferrals: 2 },
  { rank: "Team Leader", levelMin: 6, directReferrals: 4 },
  { rank: "Silver Executive", levelMin: 9, directReferrals: 6 },
  { rank: "Gold Executive", levelMin: 12, directReferrals: 8 },
  { rank: "Diamond Executive", levelMin: 15, directReferrals: 10 },
];

function highestQualifiedRankBarSegmentIndex(userLevel, directRefCount) {
  const lv = Math.max(0, Number(userLevel) || 0);
  const refs = Math.max(0, Number(directRefCount) || 0);
  let k = -1;
  for (let i = 0; i < RANK_LADDER_SEGMENTS.length; i++) {
    const row = RANK_LADDER_SEGMENTS[i];
    if (lv >= row.levelMin && refs >= row.directReferrals) {
      k = i;
    }
  }
  return k;
}

/**
 * Same title as My Activity header chips — qualifies by network depth + direct refs (not stale Users.DesignationName).
 */
function highestQualifiedRankNameForDisplay(userLevel, directRefCount, profileOk, referralsDataReady) {
  if (!referralsDataReady || !profileOk) return null;
  const k = highestQualifiedRankBarSegmentIndex(userLevel, directRefCount);
  if (k < 0) return null;
  return RANK_LADDER_SEGMENTS[k]?.rank ?? null;
}

function directReferralCountFromRecord(record) {
  const raw =
    record?.DirectReferrals ??
    record?.directReferrals ??
    record?.DirectReferralCount ??
    record?.directReferralCount ??
    record?.ReferralCount ??
    record?.referralCount ??
    record?.Referrals ??
    record?.referrals ??
    null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

function referralCodeFromRecord(record) {
  return String(
    record?.ReferralCode ??
      record?.referralCode ??
      record?.ReferralCodeNormalized ??
      record?.referralCodeNormalized ??
      ""
  ).trim();
}

function directReferralCountFromDirectReferralsResponse(data) {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.referrals)) return data.referrals.length;
  if (Array.isArray(data.flat)) {
    const direct = data.flat.filter((row) => Number(row?.Level ?? row?.level ?? 0) === 1);
    return direct.length;
  }
  return null;
}

function directReferralCountFromOriginalReferralsResponse(data) {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.referrals)) return data.referrals.length;
  if (Array.isArray(data.Referrals)) return data.Referrals.length;
  return null;
}

function referralCodeFromLinkPayload(payload) {
  if (!payload || typeof payload !== "object") return "";
  const direct = String(
    payload.ReferralCode ??
      payload.referralCode ??
      payload.code ??
      payload.ReferralLink ??
      payload.referralLink ??
      payload.link ??
      ""
  ).trim();
  if (!direct) return "";
  try {
    const url = new URL(direct);
    return String(url.searchParams.get("ref") ?? url.searchParams.get("REF") ?? "").trim();
  } catch {
    const match = direct.match(/[?&]ref=([^&]+)/i);
    return match ? decodeURIComponent(match[1]) : direct;
  }
}

function resolveRuleBasedRank(record, designationByUserId, userId, levelByUserId) {
  const uid = Number(userId);
  const refLevel = resolveUserProgramLevel(record, null, levelByUserId, uid);
  const refDirectCount = directReferralCountFromRecord(record);
  if (refDirectCount !== null) {
    return {
      rank:
        highestQualifiedRankNameForDisplay(refLevel, refDirectCount, isProfileCompleteForRanks(record), true) ??
        "Member",
      level: refLevel,
      directRefCount: refDirectCount,
    };
  }
  const fallbackRank = resolveRankLabel(record, designationByUserId, uid, false);
  return {
    rank: fallbackRank,
    level: refLevel,
    directRefCount: 0,
  };
}

function isProfileCompleteForRanks(profileUser) {
  if (!profileUser || typeof profileUser !== "object") return false;
  const first = String(profileUser.FirstName ?? profileUser.firstName ?? "").trim();
  const last = String(profileUser.LastName ?? profileUser.lastName ?? "").trim();
  const email = String(profileUser.Email ?? profileUser.email ?? "").trim();
  return Boolean(first && last && email);
}

const USE_BINARY_COMPLETE_PLACEMENT =
  process.env.REACT_APP_USE_BINARY_COMPLETE_PLACEMENT === "true";

function maxChainPlacementDepthFromFlat(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return 0;
  let m = 0;
  for (const r of flat) {
    const lv = Number(r.Level ?? r.level ?? 0) || 0;
    if (lv > m) m = lv;
  }
  return m;
}

function maxDepthFromNestedReferralNodes(nodes, depth = 1) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let best = depth;
  for (const n of nodes) {
    const children = n.children;
    if (Array.isArray(children) && children.length > 0) {
      best = Math.max(best, maxDepthFromNestedReferralNodes(children, depth + 1));
    }
  }
  return best;
}

function maxBinaryCompletePlacementDepthFromFlat(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return 0;
  const counts = new Map();
  for (const r of flat) {
    const lv = Number(r.Level ?? r.level ?? 0) || 0;
    if (lv < 1 || lv > 15) continue;
    counts.set(lv, (counts.get(lv) || 0) + 1);
  }
  for (let d = 1; d <= 15; d += 1) {
    const need = 2 ** d;
    const have = counts.get(d) ?? 0;
    if (have !== need) return d - 1;
  }
  return 15;
}

function maxBinaryCompletePlacementDepthFromNested(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let frontier = nodes;
  for (let d = 1; d <= 15; d += 1) {
    const need = 2 ** d;
    if (frontier.length !== need) return d - 1;
    const next = [];
    for (const n of frontier) {
      const ch = n.children;
      if (Array.isArray(ch)) next.push(...ch);
    }
    frontier = next;
  }
  return 15;
}

function maxPlacementDepthFromDirectReferralsResponse(data) {
  if (!data || typeof data !== "object") return 0;
  const flat = data.flat;
  if (Array.isArray(flat) && flat.length > 0) {
    return USE_BINARY_COMPLETE_PLACEMENT
      ? maxBinaryCompletePlacementDepthFromFlat(flat)
      : maxChainPlacementDepthFromFlat(flat);
  }
  const refs = data.referrals;
  if (!Array.isArray(refs) || refs.length === 0) return 0;
  return USE_BINARY_COMPLETE_PLACEMENT
    ? maxBinaryCompletePlacementDepthFromNested(refs)
    : maxDepthFromNestedReferralNodes(refs, 1);
}

/** Compact label: `hostname/refCode` from `?ref=` — full URL still used for copy/open. */
function referralLinkShortDisplay(fullUrl) {
  if (!fullUrl || typeof fullUrl !== "string") return "";
  try {
    const u = new URL(fullUrl);
    const host = u.hostname.replace(/^www\./i, "");
    const ref = u.searchParams.get("ref") ?? u.searchParams.get("REF");
    if (host && ref) {
      return `${host}/${ref}`;
    }
    const path = u.pathname.replace(/^\/+/, "");
    const fallback = `${path}${u.search || ""}`;
    return fallback || fullUrl;
  } catch {
    return fullUrl;
  }
}

function extractNumericLevelFromRecord(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = [
    "Level",
    "level",
    "UserLevel",
    "userLevel",
    "CurrentLevel",
    "currentLevel",
    "MarketingLevel",
    "marketingLevel",
    "ReferralProgramLevel",
    "referralProgramLevel",
  ];
  for (const k of keys) {
    if (obj[k] == null || obj[k] === "") continue;
    const n = Number(obj[k]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function resolveUserProgramLevel(profileUser, jwtPayload, levelByUserId, selfUserId) {
  const fromMap =
    selfUserId != null && levelByUserId && typeof levelByUserId === "object"
      ? levelByUserId[Number(selfUserId)]
      : null;
  const fromMapNum = fromMap != null ? Number(fromMap) : NaN;
  const merged = {
    ...(profileUser && typeof profileUser === "object" ? profileUser : {}),
    ...(jwtPayload && typeof jwtPayload === "object" ? jwtPayload : {}),
  };
  const fromProfile = extractNumericLevelFromRecord(merged);
  const candidates = [];
  if (Number.isFinite(fromMapNum)) candidates.push(fromMapNum);
  if (fromProfile != null && Number.isFinite(fromProfile)) candidates.push(fromProfile);
  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
}

/** Small caps label for sub-sections inside dashboard cards */
function SectionEyebrow({ children, sx = {} }) {
  return (
    <Typography
      variant="overline"
      component="p"
      sx={{
        m: 0,
        letterSpacing: "0.14em",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: "text.secondary",
        opacity: 0.88,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/** One row (name | pills) until the name would ellipsize; then stacks pills on the next row so the full name can show. Per-card only. */
function ReferralCardNamePillsRow({ referral, theme, isDark, referralLayoutChars }) {
  const containerRef = useRef(null);
  const nameRef = useRef(null);
  const [stackPills, setStackPills] = useState(false);
  const [layoutTick, setLayoutTick] = useState(0);

  const nameText = String(referral.name ?? "").trim() || "—";

  const measureShouldStack = useCallback(() => {
    const el = nameRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 0.5) {
      setStackPills(true);
    }
  }, []);

  useLayoutEffect(() => {
    setStackPills(false);
  }, [nameText, referral.userId]);

  useLayoutEffect(() => {
    if (stackPills) return;
    measureShouldStack();
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      measureShouldStack();
      raf2 = requestAnimationFrame(measureShouldStack);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
    };
  }, [stackPills, nameText, referral.rank, referral.currentStreak, layoutTick, measureShouldStack]);

  useEffect(() => {
    const onResize = () => {
      setStackPills(false);
      setLayoutTick((n) => n + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) {
        setStackPills(false);
        setLayoutTick((n) => n + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [nameText]);

  const pills = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="nowrap"
      spacing={1}
      sx={{
        gap: { xs: 0.45, sm: 0.65 },
        flexShrink: 0,
        width: stackPills ? "100%" : "auto",
        minWidth: 0,
        justifySelf: "end",
      }}
    >
      <Box
        sx={{
          border: "1px solid",
          borderColor: alpha(theme.palette.success.main, isDark ? 0.35 : 0.22),
          borderRadius: 999,
          px: { xs: 0.55, sm: 0.9, md: 1.1 },
          py: 0.45,
          flexShrink: 0,
          maxWidth: "100%",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#152018", 0.95)} 0%, ${alpha("#0f1510", 0.98)} 100%)`
            : alpha(theme.palette.success.main, 0.07),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: theme.palette.success.light,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "right",
            letterSpacing: "0.02em",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            maxWidth: {
              xs: "min(9.5rem, 36vw)",
              md: `${referralLayoutChars.rankChars}ch`,
            },
          }}
        >
          {referral.rank}
        </Typography>
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: isStreakGoalMetToday(referral.adsWatchedToday, referral.goalPerDay)
            ? alpha(theme.palette.divider, isDark ? 0.35 : 0.2)
            : alpha(theme.palette.warning.main, 0.45),
          borderRadius: 999,
          px: { xs: 0.55, sm: 0.9, md: 1.1 },
          py: 0.45,
          flexShrink: 0,
          maxWidth: "100%",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#1c1618", 0.95)} 0%, ${alpha("#141012", 0.98)} 100%)`
            : alpha(theme.palette.background.paper, 0.95),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "common.white",
            textAlign: "right",
            letterSpacing: "0.03em",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            "& > .home-streak-fire": {
              ...homeStreakPulseKeyframes,
              animation: "homeStreakPulse 2.1s ease-in-out infinite",
              display: "inline-block",
              transformOrigin: "center",
            },
          }}
        >
          <span>
            {Number.isFinite(Number(referral.currentStreak))
              ? Number(referral.currentStreak).toLocaleString()
              : "..."}
          </span>
          <span className="home-streak-fire" aria-hidden>🔥</span>
        </Typography>
      </Box>
    </Stack>
  );

  if (stackPills) {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.85,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          ref={nameRef}
          variant="body2"
          component="span"
          sx={{
            fontWeight: 700,
            color: "common.white",
            width: "100%",
            display: "block",
            whiteSpace: "normal",
            overflow: "visible",
            wordBreak: "break-word",
            textDecoration: "none",
            letterSpacing: "0.02em",
            fontSize: "0.9375rem",
            textAlign: "left",
          }}
        >
          {nameText}
        </Typography>
        {pills}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        columnGap: { xs: 0.5, sm: 0.75 },
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        ref={nameRef}
        variant="body2"
        component="span"
        sx={{
          fontWeight: 700,
          color: "common.white",
          minWidth: 0,
          maxWidth: "100%",
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textDecoration: "none",
          letterSpacing: "0.02em",
          fontSize: "0.9375rem",
          textAlign: "left",
        }}
      >
        {nameText}
      </Typography>
      {pills}
    </Box>
  );
}

function ThinLadderBar({ theme, emoji, fillPercent, label }) {
  const fp = Math.min(100, Math.max(0, fillPercent));
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
      <Typography
        component="span"
        sx={{
          fontSize: "1.1rem",
          lineHeight: 1,
          flexShrink: 0,
          opacity: 0.95,
          filter: isDark ? `drop-shadow(0 0 10px ${alpha(portal.cyan, 0.4)})` : "none",
          ...homeIconFloatKeyframes,
          animation: "homeIconFloat 3.6s ease-in-out infinite",
          willChange: "transform",
        }}
        aria-hidden
      >
        {emoji}
      </Typography>
      <Box
        component="span"
        sx={{ position: "relative", flex: 1, minWidth: 0 }}
        aria-label={label}
      >
        <Box
          sx={{
            height: 8,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.07 : 0.06),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, isDark ? 0.5 : 0.12),
            overflow: "hidden",
            position: "relative",
            boxShadow: isDark
              ? `inset 0 1px 3px ${alpha("#000", 0.45)}, 0 0 0 1px ${alpha("#fff", 0.02)}`
              : `inset 0 1px 1px ${alpha("#000", 0.04)}`,
          }}
        >
          <Box
            sx={{
              width: `${fp}%`,
              height: "100%",
              borderRadius: "inherit",
              background: portal.gradientBar,
              backgroundSize: "200% 100%",
              position: "relative",
              overflow: "hidden",
              transition: "width 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                fp > 0
                  ? `0 0 16px ${alpha(portal.cyan, isDark ? 0.5 : 0.28)}, 0 0 32px ${alpha(portal.pink, isDark ? 0.2 : 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.22)}`
                  : "none",
              "&::after":
                fp > 5
                  ? {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "40%",
                      height: "100%",
                      background: `linear-gradient(90deg, ${alpha("#fff", 0)} 0%, ${alpha("#fff", 0.55)} 50%, ${alpha("#fff", 0)} 100%)`,
                      ...homeProgressShimmerKeyframes,
                      animation: "homeProgressShimmer 2.4s ease-in-out infinite",
                      pointerEvents: "none",
                    }
                  : undefined,
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: alpha("#fff", 0.06),
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function getInitialHomeStateFromCache() {
  const uid = getUserIdFromToken();
  if (!uid) {
    return { loading: false, selfInfo: null, referrals: [], error: "" };
  }
  const c = readHomeCache(uid);
  const persistentRankProgress = readPersistentHomeRankProgressCache(uid);
  const persistentReferrals = Object.values(persistentRankProgress?.referralsByUserId ?? {});
  if (shouldForceUserDashboardHomeNetwork()) {
    return {
      loading: true,
      selfInfo: c?.selfInfo ?? buildFallbackHomeSelfInfo(uid, c?.raw),
      referrals: c?.referrals?.length ? c.referrals : persistentReferrals,
      error: "",
    };
  }
  // Only render the "all done" state when the cached bundle has every
  // expected API payload. Partial caches force a loading state so the
  // fetch flow re-runs and the user sees animations instead of empty
  // data left over from a previous flaky load.
  if (isHomeCacheComplete(c)) {
    return {
      loading: false,
      selfInfo: c.selfInfo,
      referrals: c.referrals?.length ? c.referrals : persistentReferrals,
      error: "",
    };
  }
  return {
    loading: true,
    selfInfo: c?.selfInfo ?? buildFallbackHomeSelfInfo(uid, c?.raw),
    referrals: c?.referrals?.length ? c.referrals : persistentReferrals,
    error: "",
  };
}

function getInitialHomeVisualCardLoading(isLoading) {
  return {
    coins: Boolean(isLoading),
    ledger: Boolean(isLoading),
    self: Boolean(isLoading),
    referrals: Boolean(isLoading),
  };
}

async function mapWithConcurrency(items, limit, mapper) {
  const source = Array.isArray(items) ? items : [];
  const results = new Array(source.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, limit), source.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < source.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await mapper(source[currentIndex], currentIndex);
      }
    })
  );
  return results;
}

function deferHomeBackgroundWork(callback, { preferImmediate = false } = {}) {
  if (preferImmediate) {
    setTimeout(callback, 0);
    return;
  }
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 1500 });
    return;
  }
  setTimeout(callback, 500);
}

function isPersistentReferralFreshForCurrentSlot(row) {
  if (!row || typeof row !== "object") return false;
  return Number(row.rankProgressSlotStartMs) === getCurrentUtcSixHourSlotStartMs();
}

function shouldRefreshReferralRank(row) {
  if (!isPersistentReferralFreshForCurrentSlot(row)) return true;
  const rank = String(row?.rank ?? "").trim();
  const hasLevelsProgress = Number.isFinite(Number(row?.levelsBarPercent));
  const hasReferralsProgress = Number.isFinite(Number(row?.referralsBarPercent));
  return !rank || rank.toLowerCase() === "member" || !hasLevelsProgress || !hasReferralsProgress;
}

function keepCachedProgressUntilFresh(cachedValue, freshValue) {
  const cachedNumber = Number(cachedValue);
  if (Number.isFinite(cachedNumber)) return cachedNumber;
  const freshNumber = Number(freshValue);
  return Number.isFinite(freshNumber) ? freshNumber : 0;
}

export default function Home() {
  const theme = useTheme();
  const utcMidnightRemainingMs = useUtcMidnightCountdown();
  const _init = getInitialHomeStateFromCache();
  const [loading, setLoading] = useState(_init.loading);
  const [visualCardLoading, setVisualCardLoading] = useState(() => getInitialHomeVisualCardLoading(_init.loading));
  const [error, setError] = useState(_init.error);
  const [selfInfo, setSelfInfo] = useState(_init.selfInfo);
  const [referrals, setReferrals] = useState(_init.referrals);
  const [copySnack, setCopySnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Long-press support for the referral link — see Link onTouchStart below.
  const referralLinkLongPressTimerRef = useRef(null);
  const referralLinkLongPressFiredRef = useRef(false);
  useEffect(
    () => () => {
      if (referralLinkLongPressTimerRef.current) {
        clearTimeout(referralLinkLongPressTimerRef.current);
      }
    },
    []
  );
  // Auto-dismiss the centred "Link copied" toast after a short delay.
  useEffect(() => {
    if (!copySnack.open) return undefined;
    const t = setTimeout(
      () => setCopySnack((s) => ({ ...s, open: false })),
      1800
    );
    return () => clearTimeout(t);
  }, [copySnack.open]);
  const copyToClipboard = useCallback(async (text, successMessage) => {
    if (!text) return;
    const showSuccess = () =>
      setCopySnack({
        open: true,
        message: successMessage || "Copied",
        severity: "success",
      });
    // 1) Modern Clipboard API (only works in secure contexts).
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showSuccess();
        return;
      }
    } catch {
      /* fall through to legacy path */
    }
    // 2) Legacy fallback — works on older browsers / non-HTTPS.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      ta.style.pointerEvents = "none";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        showSuccess();
      } else {
        throw new Error("execCommand returned false");
      }
    } catch {
      setCopySnack({
        open: true,
        message: "Could not copy",
        severity: "error",
      });
    }
  }, []);

  const referralLayoutChars = useMemo(() => {
    if (!Array.isArray(referrals) || referrals.length === 0) {
      return { rankChars: 6 };
    }
    const maxRank = referrals.reduce(
      (m, r) => Math.max(m, Math.min(16, String(r?.rank ?? "").length)),
      0
    );
    return {
      rankChars: Math.max(1, maxRank),
    };
  }, [referrals]);

  const selfStatsLayoutChars = useMemo(() => {
    if (!selfInfo) return { rankChars: 6 };
    const rankLen = String(selfInfo.rank ?? "").length;
    return {
      rankChars: Math.max(1, Math.min(16, rankLen)),
    };
  }, [selfInfo]);

  const anyDownlineAdGoalOpen = useMemo(
    () =>
      referrals.some((r) => !isStreakGoalMetToday(r.adsWatchedToday, r.goalPerDay)),
    [referrals]
  );

  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  const userId = useMemo(() => getUserIdFromToken(), [token]);

  const fetchHomeData = useCallback(async () => {
    if (!userId) {
      setError("Please sign in to view Home.");
      setLoading(false);
      return;
    }

    const forceNetwork = shouldForceUserDashboardHomeNetwork();
    const cached = readHomeCache(userId);
    // Stale-while-revalidate: never return early on cache alone. A prior fetch in this UTC
    // slot can have cached zeros (e.g. transient 401s) or only some of the sub-requests
    // (flaky mobile network), and skipping the network until the next slot would leave the
    // dashboard stuck at 0. So always fall through to the live fetch flow — but if the
    // cached bundle is *complete*, do it silently (no loading state) so the user keeps
    // seeing real numbers while we refresh in the background.
    const silentRefresh =
      !forceNetwork && Boolean(cached?.selfInfo) && isHomeCacheComplete(cached);

    if (cached?.selfInfo) {
      setSelfInfo(cached.selfInfo);
      setReferrals(cached.referrals);
    } else if (cached?.raw) {
      setSelfInfo(buildFallbackHomeSelfInfo(userId, cached.raw));
      setReferrals(cached.referrals);
    }

    setError("");
    if (silentRefresh) {
      setVisualCardLoading(getInitialHomeVisualCardLoading(false));
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const rawCache = readHomeCache(userId)?.raw;
      const sessionProfile = readUserSessionProfileCache(userId);
      const persistentRankProgress = readPersistentHomeRankProgressCache(userId);
      const persistentReferralsByUserId = persistentRankProgress?.referralsByUserId ?? {};
      let currentSelfInfo = cached?.selfInfo ?? buildFallbackHomeSelfInfo(userId, cached?.raw);
      let currentReferrals = cached?.referrals?.length
        ? cached.referrals
        : Object.values(persistentReferralsByUserId);
      const hasCachedRaw = (key) =>
        rawCache && Object.prototype.hasOwnProperty.call(rawCache, key);
      setVisualCardLoading({
        coins: !hasCachedRaw("coins"),
        ledger: !hasCachedRaw("ledger"),
        self: !(hasCachedRaw("profile") && hasCachedRaw("streak") && hasCachedRaw("refs") && hasCachedRaw("levels")),
        referrals: !(hasCachedRaw("refs") && hasCachedRaw("levels")),
      });
      const updateSelfInfo = (partial) => {
        currentSelfInfo = { ...(currentSelfInfo ?? buildFallbackHomeSelfInfo(userId)), ...partial };
        setSelfInfo(currentSelfInfo);
        mergeHomeCacheSelfInfo(userId, partial);
      };
      const updateReferralRowLive = (rid, partial) => {
        if (!Number.isFinite(Number(rid)) || !partial || typeof partial !== "object") return;
        const numericRid = Number(rid);
        const rows = Array.isArray(currentReferrals) ? currentReferrals : [];
        currentReferrals = rows.map((row) =>
          Number(row.userId) === numericRid ? { ...row, ...partial, userId: numericRid } : row
        );
        setReferrals(currentReferrals);
        writeHomeCache(userId, currentSelfInfo, currentReferrals);
      };
      const fetchJsonWithCache = async (key, request) => {
        if (key === "profile" && sessionProfile?.profile) {
          return { ok: true, data: sessionProfile.profile };
        }
        if (hasCachedRaw(key)) {
          return { ok: true, data: rawCache[key] };
        }
        try {
          const response = await request();
          const data = await response.json().catch(() => ({}));
          if (response.ok) {
            mergeDashboardRawFields(userId, { [key]: data });
            if (key === "profile") {
              writeUserSessionProfileCache(userId, data);
            }
          }
          return { ok: response.ok, data };
        } catch (requestError) {
          return { ok: false, data: { message: requestError?.message || "Network request failed" } };
        }
      };

      const profilePromise = fetchJsonWithCache("profile", () => fetch(API.userProfile(userId), { headers }));
      const ledgerPromise = fetchJsonWithCache("ledger", () => {
        const { from, to } = getCurrentMonthDateRange();
        return fetch(buildLedgerUrl(userId, from, to), { headers });
      });
      const streakPromise = fetchJsonWithCache("streak", () =>
        fetch(`${API.adsStreak}?userId=${encodeURIComponent(userId)}`, { headers })
      );
      const refsPromise = fetchJsonWithCache("refs", () => fetch(API.originalReferrals(userId), { headers }));
      const levelsPromise = fetchJsonWithCache("levels", () => fetch(API.getUsersByLevel, { headers }));
      const coinsPromise = fetchJsonWithCache("coins", () => fetch(API.appCoinsApps, { headers }));
      const streakCompletersPromise = fetchJsonWithCache("streakCompleters", () =>
        fetch(API.directReferralsStreakCompleters, { headers })
      );

      profilePromise.then((profileResult) => {
        if (!profileResult.ok) return;
        const me = profileResult.data?.user ?? profileResult.data ?? {};
        let jwtPayload = null;
        try {
          jwtPayload = token ? jwtDecode(token) : null;
        } catch {
          jwtPayload = null;
        }
        const refCode = String(
          me?.ReferralCode ??
            me?.referralCode ??
            jwtPayload?.referralCode ??
            jwtPayload?.ReferralCode ??
            ""
        ).trim();
        updateSelfInfo({
          name: displayNameFromRecord(me, "You"),
          referralLink: refCode ? `https://dv-apps.com/signup?ref=${encodeURIComponent(refCode)}` : "",
        });
      });

      ledgerPromise.then((ledgerResult) => {
        setVisualCardLoading((prev) => ({ ...prev, ledger: false }));
        if (!ledgerResult.ok) return;
        updateSelfInfo({
          engagementPoints: sumEngagementPointsFromLedgerPayload(ledgerResult.data),
        });
      });

      coinsPromise.then((coinsResult) => {
        setVisualCardLoading((prev) => ({ ...prev, coins: false }));
        if (!coinsResult.ok || !coinsResult.data?.success) return;
        updateSelfInfo({
          totalDreameyDiamonds: dreameyDiamondsUserSum(coinsResult.data),
        });
      });

      streakPromise.then((streakResult) => {
        if (!streakResult.ok) return;
        const streakNorm = normalizeStreakPayload(streakResult.data);
        const todayGoalPercent =
          (streakNorm?.goalPerDay ?? 0) > 0
            ? Math.min(100, ((streakNorm?.adsWatchedToday ?? 0) / (streakNorm?.goalPerDay ?? 1)) * 100)
            : 0;
        updateSelfInfo({
          currentStreak: streakNorm?.currentStreak ?? 0,
          todayGoalPercent,
          adsWatchedToday: streakNorm?.adsWatchedToday ?? 0,
          goalPerDay: streakNorm?.goalPerDay ?? 10,
        });
      });

      Promise.all([refsPromise, levelsPromise]).then(([refsResultEarly, levelsResultEarly]) => {
        if (!refsResultEarly.ok) return;
        const directRefsEarly = Array.isArray(refsResultEarly.data?.referrals)
          ? refsResultEarly.data.referrals
          : [];
        const levelByUserIdEarly = {};
        const designationByUserIdEarly = {};
        if (levelsResultEarly.ok && levelsResultEarly.data?.success && Array.isArray(levelsResultEarly.data?.users)) {
          for (const u of levelsResultEarly.data.users) {
            const uid = Number(u?.UserID ?? u?.userId);
            if (!Number.isFinite(uid)) continue;
            levelByUserIdEarly[uid] = Number(u?.Level ?? u?.level ?? 0) || 0;
            const dn = designationNameFromRecord(u);
            if (dn) designationByUserIdEarly[uid] = dn;
          }
        }
        const earlyRows = directRefsEarly.map((ref) => {
          const rid = Number(ref?.UserID ?? ref?.userId);
          if (!Number.isFinite(rid)) return null;
          const persistentRow = persistentReferralsByUserId[rid];
          const { rank, level: refLevel, directRefCount: refDirectCount } =
            resolveRuleBasedRank(ref, designationByUserIdEarly, rid, levelByUserIdEarly);
          return {
            ...(persistentRow && typeof persistentRow === "object" ? persistentRow : {}),
            userId: rid,
            name: displayNameFromRecord(ref),
            rank: rank && rank !== "Member" ? rank : persistentRow?.rank ?? rank,
            engagementPoints: 0,
            currentStreak: null,
            adsWatchedToday: null,
            goalPerDay: 10,
            todayGoalPercent: 0,
            levelsBarPercent: keepCachedProgressUntilFresh(
              persistentRow?.levelsBarPercent,
              rankLevelsToBarFillPercent(refLevel)
            ),
            referralsBarPercent: keepCachedProgressUntilFresh(
              persistentRow?.referralsBarPercent,
              rankReferralsToBarFillPercent(refDirectCount)
            ),
          };
        }).filter(Boolean);
        currentReferrals = earlyRows;
        setReferrals(earlyRows);
        writeHomeCache(userId, currentSelfInfo, earlyRows);
      });

      const [
        profileResult,
        ledgerResult,
        streakResult,
        refsResult,
        levelsResult,
        coinsResult,
      ] = await Promise.all([
        profilePromise,
        ledgerPromise,
        streakPromise,
        refsPromise,
        levelsPromise,
        coinsPromise,
      ]);

      const profileRes = { ok: profileResult.ok };
      const ledgerRes = { ok: ledgerResult.ok };
      const streakRes = { ok: streakResult.ok };
      const refsRes = { ok: refsResult.ok };
      const levelsRes = { ok: levelsResult.ok };
      const coinsRes = { ok: coinsResult.ok };
      const profileData = profileResult.data;
      const ledgerData = ledgerResult.data;
      const streakData = streakResult.data;
      const refsData = refsResult.data;
      const levelsData = levelsResult.data;
      const coinsData = coinsResult.data;

      if (!profileRes.ok) {
        setError(profileData?.message || "Failed to load profile");
      }

      const me = profileData?.user ?? profileData ?? {};
      const previousSelfInfo = currentSelfInfo ?? cached?.selfInfo ?? buildFallbackHomeSelfInfo(userId, cached?.raw);
      const engagementPoints = ledgerRes.ok
        ? sumEngagementPointsFromLedgerPayload(ledgerData)
        : Number(previousSelfInfo?.engagementPoints ?? 0) || 0;
      const streakNorm = streakRes.ok ? normalizeStreakPayload(streakData) : null;
      const myStreak = streakRes.ok
        ? streakNorm?.currentStreak ?? 0
        : Number(previousSelfInfo?.currentStreak ?? 0) || 0;
      const directRefs = Array.isArray(refsData?.referrals) ? refsData.referrals : [];

      const levelByUserId = {};
      const designationByUserId = {};
      if (levelsRes.ok && levelsData?.success && Array.isArray(levelsData?.users)) {
        for (const u of levelsData.users) {
          const uid = Number(u?.UserID ?? u?.userId);
          if (!Number.isFinite(uid)) continue;
          levelByUserId[uid] = Number(u?.Level ?? u?.level ?? 0) || 0;
          const dn = designationNameFromRecord(u);
          if (dn) designationByUserId[uid] = dn;
        }
      }

      let jwtPayload = null;
      try {
        jwtPayload = token ? jwtDecode(token) : null;
      } catch {
        jwtPayload = null;
      }

      const hasCachedPlacementMaxDepth = hasCachedRaw("placementMaxDepth");
      let placementMaxDepth = hasCachedPlacementMaxDepth ? rawCache.placementMaxDepth : null;
      const refCode = String(me?.ReferralCode ?? me?.referralCode ?? "").trim();
      if (!hasCachedPlacementMaxDepth && refCode) {
        try {
          const treeRes = await fetch(API.directReferrals(encodeURIComponent(refCode)), { headers });
          const treeData = await treeRes.json().catch(() => ({}));
          if (treeRes.ok) {
            placementMaxDepth = maxPlacementDepthFromDirectReferralsResponse(treeData);
            mergeDashboardRawFields(userId, { placementMaxDepth });
          }
        } catch {
          /* network tree optional for home bars */
        }
      }

      const legacyProgramLevel = resolveUserProgramLevel(me, jwtPayload, levelByUserId, userId);
      const rankQualifyingLevel =
        placementMaxDepth != null ? Math.max(legacyProgramLevel, placementMaxDepth) : legacyProgramLevel;
      const directRefCount = directRefs.length;

      const referralsDataReady = refsRes.ok && levelsRes.ok;
      const profileOk = isProfileCompleteForRanks(me);
      const ladderRankTitle = highestQualifiedRankNameForDisplay(
        rankQualifyingLevel,
        directRefCount,
        profileOk,
        referralsDataReady
      );
      const selfRank =
        ladderRankTitle ??
        resolveRankLabel(me, designationByUserId, userId, directRefs.length > 0);
      const levelsBarPercent = rankLevelsToBarFillPercent(rankQualifyingLevel);
      const referralsBarPercent = rankReferralsToBarFillPercent(directRefCount);
      updateSelfInfo({
        rank: selfRank,
        levelsBarPercent,
        referralsBarPercent,
      });
      mergeDashboardRawFields(userId, {
        rankSummary: {
          rank: selfRank,
          rankQualifyingLevel,
          directRefCount,
          levelsBarPercent,
          referralsBarPercent,
        },
      });
      mergePersistentHomeRankProgressCache(userId, {
        selfRankSummary: {
          rank: selfRank,
          rankQualifyingLevel,
          directRefCount,
          levelsBarPercent,
          referralsBarPercent,
        },
      });
      const todayGoalPercent = streakRes.ok
        ? (streakNorm?.goalPerDay ?? 0) > 0
          ? Math.min(100, ((streakNorm?.adsWatchedToday ?? 0) / (streakNorm?.goalPerDay ?? 1)) * 100)
          : 0
        : Number(previousSelfInfo?.todayGoalPercent ?? 0) || 0;

      const myReferralCode = (
        refCode ||
        String(jwtPayload?.referralCode ?? jwtPayload?.ReferralCode ?? "").trim()
      );
      const myReferralLink = myReferralCode
        ? `https://dv-apps.com/signup?ref=${encodeURIComponent(myReferralCode)}`
        : "";

      let totalDreameyDiamonds = Number(previousSelfInfo?.totalDreameyDiamonds ?? 0) || 0;
      if (coinsRes.ok && coinsData?.success) {
        totalDreameyDiamonds = dreameyDiamondsUserSum(coinsData);
      }

      const nextSelfInfoBase = {
        name: displayNameFromRecord(me, "You"),
        rank: selfRank,
        engagementPoints,
        currentStreak: myStreak,
        levelsBarPercent,
        referralsBarPercent,
        todayGoalPercent,
        adsWatchedToday: streakRes.ok
          ? streakNorm?.adsWatchedToday ?? 0
          : Number(previousSelfInfo?.adsWatchedToday ?? 0) || 0,
        goalPerDay: streakRes.ok
          ? streakNorm?.goalPerDay ?? 10
          : Number(previousSelfInfo?.goalPerDay ?? 10) || 10,
        referralLink: myReferralLink,
        totalDreameyDiamonds,
        activeConnections: 0,
      };
      setSelfInfo(nextSelfInfoBase);

      const nextSelfInfo = nextSelfInfoBase;
      currentReferrals = directRefs.map((ref) => {
        const rid = Number(ref?.UserID ?? ref?.userId);
        if (!Number.isFinite(rid)) return null;
        const persistentRow = persistentReferralsByUserId[rid];
        const { rank, level: refLevel, directRefCount: refDirectCount } =
          resolveRuleBasedRank(ref, designationByUserId, rid, levelByUserId);
        return {
          ...(persistentRow && typeof persistentRow === "object" ? persistentRow : {}),
          userId: rid,
          name: displayNameFromRecord(ref),
          rank: rank && rank !== "Member" ? rank : persistentRow?.rank ?? rank,
          engagementPoints: 0,
          currentStreak: null,
          adsWatchedToday: null,
          goalPerDay: 10,
          todayGoalPercent: 0,
          levelsBarPercent: keepCachedProgressUntilFresh(
            persistentRow?.levelsBarPercent,
            rankLevelsToBarFillPercent(refLevel)
          ),
          referralsBarPercent: keepCachedProgressUntilFresh(
            persistentRow?.referralsBarPercent,
            rankReferralsToBarFillPercent(refDirectCount)
          ),
        };
      }).filter(Boolean);
      setReferrals(currentReferrals);
      mergePersistentHomeRankProgressCache(userId, {
        referralsByUserId: Object.fromEntries(currentReferrals.map((row) => [row.userId, row])),
      });
      setVisualCardLoading((prev) => ({ ...prev, self: false }));
      writeHomeCache(userId, nextSelfInfo, currentReferrals, {
        profile: profileData,
        ledger: ledgerData,
        streak: streakData,
        refs: refsData,
        levels: levelsData,
        coins: coinsData,
        rankSummary: {
          rank: selfRank,
          rankQualifyingLevel,
          directRefCount,
          levelsBarPercent,
          referralsBarPercent,
        },
        placementMaxDepth,
      });
      clearUserDashboardHomeForceNetwork();

      const referralsNeedingDailyRefresh = directRefs;
      const referralsNeedingRankEnrichment = directRefs.filter((ref) => {
        const rid = Number(ref?.UserID ?? ref?.userId);
        if (!Number.isFinite(rid)) return false;
        return shouldRefreshReferralRank(persistentReferralsByUserId[rid]);
      });
      const manyDirectReferrals = directRefs.length >= HOME_MANY_REFERRALS_THRESHOLD;
      const shouldAnimateReferralLoading =
        !manyDirectReferrals &&
        (referralsNeedingDailyRefresh.length > 0 ||
          referralsNeedingRankEnrichment.length > 0 ||
          !hasCachedRaw("streakCompleters"));
      setVisualCardLoading((prev) => ({ ...prev, referrals: shouldAnimateReferralLoading }));

      deferHomeBackgroundWork(() => {
      void Promise.all([
        streakCompletersPromise,
        mapWithConcurrency(referralsNeedingDailyRefresh, 12, async (ref) => {
          const rid = Number(ref?.UserID ?? ref?.userId);
          if (!Number.isFinite(rid)) return null;

          const refStreakRes = await fetch(`${API.adsStreak}?userId=${encodeURIComponent(rid)}`, { headers }).catch(() => null);
          if (!refStreakRes || !refStreakRes.ok) return null;

          const refStreakData = await refStreakRes.json().catch(() => ({}));
          const refFull = normalizeStreakPayloadForRef(refStreakData);
          const refStreakNorm = normalizeStreakPayload(refStreakData);
          const refAdsWatchedToday = refStreakNorm?.adsWatchedToday ?? 0;
          const refGoalPerDay = refStreakNorm?.goalPerDay ?? 10;
          const streakRow = {
            userId: rid,
            currentStreak: refStreakNorm?.currentStreak ?? 0,
            adsWatchedToday: refAdsWatchedToday,
            goalPerDay: refGoalPerDay,
            todayGoalPercent:
              refGoalPerDay > 0 ? Math.min(100, (refAdsWatchedToday / refGoalPerDay) * 100) : 0,
          };

          updateReferralRowLive(rid, streakRow);

          return { row: streakRow, refFull };
        }),
        mapWithConcurrency(referralsNeedingRankEnrichment, 6, async (ref) => {
          const rid = Number(ref?.UserID ?? ref?.userId);
          if (!Number.isFinite(rid)) return null;

          const initialRefCode = referralCodeFromRecord(ref);
          const needsProfileForRank = !initialRefCode || !isProfileCompleteForRanks(ref);
          const [refProfileRes, refLinkRes, refOwnRefsRes] = await Promise.all([
            needsProfileForRank ? fetch(API.userProfile(rid), { headers }).catch(() => null) : Promise.resolve(null),
            !initialRefCode ? fetch(API.referralLink(rid), { headers }).catch(() => null) : Promise.resolve(null),
            fetch(API.originalReferrals(rid), { headers }).catch(() => null),
          ]);

          let refPlacementDepth = null;
          let refTreeDirectCount = null;
          let refOwnDirectCount = null;
          let refProfile = ref;
          if (refProfileRes && refProfileRes.ok) {
            const refProfileData = await refProfileRes.json().catch(() => ({}));
            const u = refProfileData?.user ?? refProfileData ?? null;
            if (u && typeof u === "object") {
              refProfile = { ...ref, ...u };
            }
          }
          let refCodeForTree = referralCodeFromRecord(refProfile) || initialRefCode;
          if (!refCodeForTree && refLinkRes && refLinkRes.ok) {
            const refLinkData = await refLinkRes.json().catch(() => ({}));
            refCodeForTree = referralCodeFromLinkPayload(refLinkData);
          }
          const refTreeRes = refCodeForTree
            ? await fetch(API.directReferrals(encodeURIComponent(refCodeForTree)), { headers }).catch(() => null)
            : null;
          if (refTreeRes && refTreeRes.ok) {
            const refTreeData = await refTreeRes.json().catch(() => ({}));
            refPlacementDepth = maxPlacementDepthFromDirectReferralsResponse(refTreeData);
            refTreeDirectCount = directReferralCountFromDirectReferralsResponse(refTreeData);
          }
          if (refOwnRefsRes && refOwnRefsRes.ok) {
            const refOwnRefsData = await refOwnRefsRes.json().catch(() => ({}));
            refOwnDirectCount = directReferralCountFromOriginalReferralsResponse(refOwnRefsData);
          }
          const ruleBase =
            resolveRuleBasedRank(refProfile, designationByUserId, rid, levelByUserId);
          const refLevel = refPlacementDepth != null
            ? Math.max(ruleBase.level, refPlacementDepth)
            : ruleBase.level;
          const refDirectCount = refOwnDirectCount ?? refTreeDirectCount ?? ruleBase.directRefCount;
          const rank =
            refOwnDirectCount !== null || refTreeDirectCount !== null || refPlacementDepth !== null
              ? highestQualifiedRankNameForDisplay(refLevel, refDirectCount, isProfileCompleteForRanks(refProfile), true) ?? "Member"
              : ruleBase.rank;

          const enrichedRow = {
            userId: rid,
            name: displayNameFromRecord(refProfile),
            rank,
            engagementPoints: 0,
            levelsBarPercent: rankLevelsToBarFillPercent(refLevel),
            referralsBarPercent: rankReferralsToBarFillPercent(refDirectCount),
            rankProgressSlotStartMs: getCurrentUtcSixHourSlotStartMs(),
          };
          setReferrals((previousRows) =>
            previousRows.map((row) => (Number(row.userId) === rid ? { ...row, ...enrichedRow } : row))
          );
          mergePersistentHomeRankProgressCache(userId, {
            referralsByUserId: { [rid]: enrichedRow },
          });
          return {
            row: {
              ...enrichedRow,
            },
          };
        })
      ]).then(([streakCompletersResult, streakResults, rankResults]) => {
        const streakCompletersRes = { ok: streakCompletersResult.ok };
        const streakCompletersData = streakCompletersResult.data;
        mergeDashboardRawFields(userId, { streakCompleters: streakCompletersData });
        const refStreakByUserId = new Map();
        const refreshedRowsById = new Map();
        const mergeRefreshedRow = (row) => {
          if (!row?.userId) return;
          refreshedRowsById.set(row.userId, {
            ...(refreshedRowsById.get(row.userId) ?? {}),
            ...row,
          });
        };
        streakResults
          .filter(Boolean)
          .map((result) => {
            if (result.refFull) refStreakByUserId.set(result.row.userId, result.refFull);
            mergeRefreshedRow(result.row);
            return result.row;
          });
        rankResults
          .filter(Boolean)
          .map((result) => {
            mergeRefreshedRow(result.row);
            return result.row;
          });
        const rows = currentReferrals.map((row) => ({
          ...row,
          ...(refreshedRowsById.get(row.userId) ?? {}),
        }));
        const goalMet = (r) => {
          const g = Math.max(1, Number(r.goalPerDay) || 10);
          return Number(r.adsWatchedToday ?? 0) >= g;
        };
        const sortedReferrals = [...rows].sort((a, b) => {
          const ma = goalMet(a);
          const mb = goalMet(b);
          if (ma !== mb) return Number(ma) - Number(mb);
          const ga = Math.max(1, Number(a.goalPerDay) || 10);
          const gb = Math.max(1, Number(b.goalPerDay) || 10);
          const ra = Number(a.adsWatchedToday ?? 0) / ga;
          const rb = Number(b.adsWatchedToday ?? 0) / gb;
          if (rb !== ra) return rb - ra;
          // When neither referral has watched any ads today, break the tie
          // by current streak (higher streak comes first).
          const adsA = Number(a.adsWatchedToday ?? 0);
          const adsB = Number(b.adsWatchedToday ?? 0);
          if (adsA === 0 && adsB === 0) {
            const streakA = Number(a.currentStreak ?? 0) || 0;
            const streakB = Number(b.currentStreak ?? 0) || 0;
            if (streakB !== streakA) return streakB - streakA;
          }
          const rankDiff = referralRankSortIndex(a.rank) - referralRankSortIndex(b.rank);
          if (rankDiff !== 0) return rankDiff;
          return (b.todayGoalPercent ?? 0) - (a.todayGoalPercent ?? 0);
        });

        let referralsActivity = null;
        if (refsRes.ok && levelsRes.ok) {
          try {
            referralsActivity = buildReferralsActivitySnapshot({
              listData: refsData,
              streakCompletersData: streakCompletersRes.ok ? streakCompletersData : {},
              levelData: levelsData,
              refStreakByUserId,
            });
          } catch {
            referralsActivity = null;
          }
        }

        setReferrals(sortedReferrals);
        mergePersistentHomeRankProgressCache(userId, {
          referralsByUserId: Object.fromEntries(sortedReferrals.map((row) => [row.userId, row])),
        });
        writeHomeCache(userId, nextSelfInfo, sortedReferrals, {
          referralsActivity,
        });
      }).finally(() => {
        setVisualCardLoading((prev) => ({ ...prev, referrals: false }));
      });
      }, { preferImmediate: manyDirectReferrals });
    } catch (e) {
      setError(e?.message || "Failed to load home data");
      setSelfInfo(buildFallbackHomeSelfInfo(userId, readHomeCache(userId)?.raw));
      setReferrals([]);
      setVisualCardLoading(getInitialHomeVisualCardLoading(false));
    } finally {
      setVisualCardLoading((prev) => ({ ...prev, coins: false, ledger: false, self: false }));
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    void fetchHomeData();
  }, [fetchHomeData]);

  const isDark = theme.palette.mode === "dark";
  const homeTrailLabelSx = {
    display: "block",
    lineHeight: 1.2,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "text.secondary",
    opacity: 0.85,
  };
  const homeGlassSurface = (extra = {}) => ({
    borderRadius: 2.5,
    px: 1.35,
    py: 0.85,
    border: "1px solid",
    borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.22 : 0.14),
    background: isDark
      ? `linear-gradient(155deg, ${alpha("#1e2436", 0.92)} 0%, ${alpha("#12151f", 0.88)} 55%, ${alpha("#0d0f16", 0.95)} 100%)`
      : alpha(theme.palette.background.paper, 0.98),
    backdropFilter: "blur(14px)",
    boxShadow: isDark
      ? `0 4px 24px ${alpha("#000", 0.35)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`
      : `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
    ...extra,
  });

  return (
    <Box
      sx={{
        maxWidth: 920,
        mx: "auto",
        py: { xs: 1.5, sm: 2.75 },
        px: { xs: 1, sm: 2 },
        background: isDark
          ? `linear-gradient(180deg, ${alpha(portal.indigo, 0.07)} 0%, transparent 32%, transparent 100%)`
          : `linear-gradient(180deg, ${alpha(portal.cyan, 0.04)} 0%, transparent 40%)`,
      }}
    >
      <DashboardPageHeader
        icon={HomeIcon}
        title="Home"
        titleTrail={
          selfInfo ? (
            <Box
              sx={{
                transform: { md: "translateY(-0.15rem)" },
                minWidth: 0,
                pt: 0,
                mt: 0,
                textAlign: "left",
                width: "fit-content",
                maxWidth: "100%",
                alignSelf: "flex-start",
                ...homeGlassSurface(),
              }}
            >
              <Typography variant="caption" sx={homeTrailLabelSx}>
                Your referral link
              </Typography>
              {selfInfo.referralLink ? (
                <Link
                  href={selfInfo.referralLink}
                  onClick={(e) => {
                    e.preventDefault();
                    // If a long-press just fired the copy, skip the tap-up
                    // click so we don't fire the toast twice.
                    if (referralLinkLongPressFiredRef.current) {
                      referralLinkLongPressFiredRef.current = false;
                      return;
                    }
                    void copyToClipboard(selfInfo.referralLink, "Referral link copied");
                  }}
                  // Block the native context menu (Android long-press,
                  // desktop right-click) and copy instead.
                  onContextMenu={(e) => {
                    e.preventDefault();
                    referralLinkLongPressFiredRef.current = true;
                    void copyToClipboard(selfInfo.referralLink, "Referral link copied");
                  }}
                  // iOS long-press detection — the OS callout is suppressed
                  // via `-webkit-touch-callout: none` below, so we run our
                  // own ~450ms timer to trigger the copy on long press.
                  onTouchStart={() => {
                    referralLinkLongPressFiredRef.current = false;
                    if (referralLinkLongPressTimerRef.current) {
                      clearTimeout(referralLinkLongPressTimerRef.current);
                    }
                    referralLinkLongPressTimerRef.current = setTimeout(() => {
                      referralLinkLongPressFiredRef.current = true;
                      void copyToClipboard(
                        selfInfo.referralLink,
                        "Referral link copied"
                      );
                    }, 450);
                  }}
                  onTouchEnd={() => {
                    if (referralLinkLongPressTimerRef.current) {
                      clearTimeout(referralLinkLongPressTimerRef.current);
                      referralLinkLongPressTimerRef.current = null;
                    }
                  }}
                  onTouchMove={() => {
                    if (referralLinkLongPressTimerRef.current) {
                      clearTimeout(referralLinkLongPressTimerRef.current);
                      referralLinkLongPressTimerRef.current = null;
                    }
                  }}
                  onTouchCancel={() => {
                    if (referralLinkLongPressTimerRef.current) {
                      clearTimeout(referralLinkLongPressTimerRef.current);
                      referralLinkLongPressTimerRef.current = null;
                    }
                  }}
                  variant="body2"
                  title={`Click or hold to copy: ${selfInfo.referralLink}`}
                  aria-label={`Copy full referral link ${selfInfo.referralLink}`}
                  sx={{
                    wordBreak: "break-all",
                    cursor: "pointer",
                    display: "inline-block",
                    mt: 0.35,
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    color: portal.cyan,
                    textDecoration: "none",
                    borderBottom: `1px solid ${alpha(portal.cyan, 0.35)}`,
                    transition: "color 0.2s, border-color 0.2s",
                    // Suppress the iOS long-press callout + text-selection
                    // menu so our custom long-press handler can take over.
                    WebkitTouchCallout: "none",
                    WebkitUserSelect: "none",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    "&:hover": {
                      color: portal.indigo,
                      borderBottomColor: alpha(portal.indigo, 0.45),
                    },
                  }}
                >
                  {referralLinkShortDisplay(selfInfo.referralLink)}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }} component="span" display="block">
                  —
                </Typography>
              )}
            </Box>
          ) : null
        }
      />

      {selfInfo ? (
        <Box sx={{ mb: 2, width: "100%" }}>
          <Box
            sx={{
              display: "grid",
              alignItems: "stretch",
              gridTemplateColumns: {
                xs: "repeat(3, minmax(0, 1fr))",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: { xs: 0.65, sm: 1.25, md: 2 },
              mb: 2.5,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <Tooltip
              title="Dreamey Coins are your future games virtual currency. After July 1, 2027, they can be used across our other apps as new experiences unlock."
              placement="top"
              enterTouchDelay={0}
            >
              <Paper
                elevation={0}
                sx={(t) => ({
                  position: "relative",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minHeight: 0,
                  p: { xs: 0.65, sm: 2.25 },
                  pt: { xs: 1, sm: 2.5 },
                  borderRadius: 3,
                  cursor: "pointer",
                  background:
                    t.palette.mode === "dark"
                      ? `linear-gradient(165deg, ${alpha("#12182a", 0.94)} 0%, ${alpha("#0b1222", 0.9)} 45%, ${alpha("#080c14", 0.95)} 100%)`
                      : `linear-gradient(165deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f9ff", 0.92)} 100%)`,
                  backdropFilter: "blur(14px)",
                  border: `1px solid ${alpha(
                    t.palette.mode === "dark" ? portal.cyan : t.palette.common.white,
                    t.palette.mode === "dark" ? 0.16 : 0.35
                  )}`,
                  boxShadow:
                    t.palette.mode === "dark"
                      ? `inset 0 1px 0 ${alpha("#fff", 0.07)}, 0 14px 36px ${alpha("#000", 0.4)}, 0 0 0 1px ${alpha("#fff", 0.03)} inset, 0 0 24px ${alpha(portal.cyan, 0.08)}`
                      : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 12px 28px ${alpha(portal.cyan, 0.1)}`,
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  transition: "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease, border-color 0.28s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: portal.gradientBar,
                    opacity: t.palette.mode === "dark" ? 0.75 : 0.5,
                    pointerEvents: "none",
                  },
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.025)",
                    borderColor: alpha(portal.amber, t.palette.mode === "dark" ? 0.55 : 0.4),
                    boxShadow:
                      t.palette.mode === "dark"
                        ? `inset 0 1px 0 ${alpha("#fff", 0.1)}, 0 22px 48px ${alpha("#000", 0.5)}, 0 0 36px ${alpha(portal.amber, 0.28)}`
                        : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 18px 36px ${alpha(portal.amber, 0.22)}`,
                  },
                  ...homeCardGradientBorderSx(t, visualCardLoading.coins),
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.4, sm: 0.9 },
                    width: "100%",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <SplitMetricValue
                    value={(Number(selfInfo.totalDreameyDiamonds) || 0) / DIAMONDS_PER_DREAMEY_COIN}
                    maxFractionDigits={2}
                    compact
                  />
                  <Box
                    sx={{
                      perspective: "120px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      filter: `drop-shadow(0 0 12px ${alpha(portal.amber, 0.55)})`,
                    }}
                  >
                    <Box
                      component="img"
                      src={DREAMEY_COIN_IMAGE_SRC}
                      alt=""
                      sx={{
                        width: { xs: 26, sm: 40 },
                        height: { xs: 26, sm: 40 },
                        objectFit: "contain",
                        display: "block",
                        ...dreameyCoinFlipAnimationSxHome,
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Tooltip>
            </Box>

            <Box sx={{ minWidth: 0, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
              <Paper
                elevation={0}
                sx={(t) => ({
                  position: "relative",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minHeight: 0,
                  p: { xs: 0.65, sm: 2.25 },
                  pt: { xs: 1, sm: 2.5 },
                  borderRadius: 3,
                  cursor: "pointer",
                  background:
                    t.palette.mode === "dark"
                      ? `linear-gradient(165deg, ${alpha("#12182a", 0.94)} 0%, ${alpha("#0b1222", 0.9)} 45%, ${alpha("#080c14", 0.95)} 100%)`
                      : `linear-gradient(165deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f9ff", 0.92)} 100%)`,
                  backdropFilter: "blur(14px)",
                  border: `1px solid ${alpha(
                    t.palette.mode === "dark" ? portal.cyan : t.palette.common.white,
                    t.palette.mode === "dark" ? 0.16 : 0.35
                  )}`,
                  boxShadow:
                    t.palette.mode === "dark"
                      ? `inset 0 1px 0 ${alpha("#fff", 0.07)}, 0 14px 36px ${alpha("#000", 0.4)}, 0 0 0 1px ${alpha("#fff", 0.03)} inset, 0 0 24px ${alpha(portal.rose, 0.08)}`
                      : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 12px 28px ${alpha(portal.cyan, 0.1)}`,
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  transition: "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease, border-color 0.28s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: portal.gradientBar,
                    opacity: t.palette.mode === "dark" ? 0.75 : 0.5,
                    pointerEvents: "none",
                  },
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.025)",
                    borderColor: alpha(portal.rose, t.palette.mode === "dark" ? 0.55 : 0.4),
                    boxShadow:
                      t.palette.mode === "dark"
                        ? `inset 0 1px 0 ${alpha("#fff", 0.1)}, 0 22px 48px ${alpha("#000", 0.5)}, 0 0 36px ${alpha(portal.rose, 0.28)}`
                        : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 18px 36px ${alpha(portal.rose, 0.22)}`,
                  },
                  ...homeCardGradientBorderSx(t, visualCardLoading.ledger),
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.4, sm: 0.9 },
                    width: "100%",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <SplitMetricValue
                    value={(Number(selfInfo.engagementPoints) || 0) / ENGAGEMENT_POINTS_PER_RUBY}
                    maxFractionDigits={2}
                    compact
                  />
                  <Box
                    component="img"
                    src={RUBY_GEM_IMAGE_SRC}
                    alt=""
                    sx={{
                      width: { xs: 22, sm: 34 },
                      height: { xs: 24, sm: 38 },
                      objectFit: "contain",
                      objectPosition: "center",
                      flexShrink: 0,
                      display: "block",
                      filter: `drop-shadow(0 0 12px ${alpha(portal.rose, 0.6)})`,
                    }}
                  />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ minWidth: 0, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <Tooltip title="These diamonds are virtual and can only be used within games." placement="top" enterTouchDelay={0}>
              <Paper
                elevation={0}
                sx={(t) => ({
                  position: "relative",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minHeight: 0,
                  p: { xs: 0.65, sm: 2.25 },
                  pt: { xs: 1, sm: 2.5 },
                  borderRadius: 3,
                  cursor: "pointer",
                  background:
                    t.palette.mode === "dark"
                      ? `linear-gradient(165deg, ${alpha("#12182a", 0.94)} 0%, ${alpha("#0b1222", 0.9)} 45%, ${alpha("#080c14", 0.95)} 100%)`
                      : `linear-gradient(165deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f9ff", 0.92)} 100%)`,
                  backdropFilter: "blur(14px)",
                  border: `1px solid ${alpha(
                    t.palette.mode === "dark" ? portal.cyan : t.palette.common.white,
                    t.palette.mode === "dark" ? 0.16 : 0.35
                  )}`,
                  boxShadow:
                    t.palette.mode === "dark"
                      ? `inset 0 1px 0 ${alpha("#fff", 0.07)}, 0 14px 36px ${alpha("#000", 0.4)}, 0 0 0 1px ${alpha("#fff", 0.03)} inset, 0 0 24px ${alpha(portal.cyan, 0.1)}`
                      : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 12px 28px ${alpha(portal.cyan, 0.1)}`,
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  transition: "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease, border-color 0.28s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: portal.gradientBar,
                    opacity: t.palette.mode === "dark" ? 0.75 : 0.5,
                    pointerEvents: "none",
                  },
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.025)",
                    borderColor: alpha(portal.cyan, t.palette.mode === "dark" ? 0.55 : 0.4),
                    boxShadow:
                      t.palette.mode === "dark"
                        ? `inset 0 1px 0 ${alpha("#fff", 0.1)}, 0 22px 48px ${alpha("#000", 0.5)}, 0 0 36px ${alpha(portal.cyan, 0.3)}`
                        : `inset 0 1px 0 ${alpha("#fff", 0.95)}, 0 18px 36px ${alpha(portal.cyan, 0.22)}`,
                  },
                  ...homeCardGradientBorderSx(t, visualCardLoading.coins),
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.4, sm: 0.9 },
                    width: "100%",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <SplitMetricValue value={Number(selfInfo.totalDreameyDiamonds) || 0} maxFractionDigits={0} compact />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: "1.15rem", sm: "1.85rem" },
                      lineHeight: 1,
                      flexShrink: 0,
                      filter: `drop-shadow(0 0 10px ${alpha(portal.cyan, 0.7)})`,
                      ...homeIconFloatKeyframes,
                      animation: "homeIconFloat 3.2s ease-in-out infinite",
                    }}
                    aria-hidden
                  >
                    💎
                  </Typography>
                </Box>
              </Paper>
            </Tooltip>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "center", px: 1 }}>
        <Paper
          component="button"
          type="button"
          onClick={openDreameyHubStore}
          aria-label="Download our app — opens the store for your device"
          elevation={0}
          sx={(theme) => ({
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.85, sm: 1.15 },
            width: "fit-content",
            maxWidth: "100%",
            textDecoration: "none",
            px: { xs: 2.25, sm: 3 },
            py: { xs: 1.35, sm: 1.65 },
            borderRadius: 999,
            cursor: "pointer",
            overflow: "hidden",
            font: "inherit",
            textAlign: "center",
            outline: "none",
            "&:focus-visible": {
              boxShadow: `0 0 0 3px ${alpha("#22c55e", 0.45)}, 0 0 0 5px ${alpha("#fff", 0.25)}`,
            },
            background: `linear-gradient(110deg, #16a34a 0%, #22c55e 30%, #10b981 55%, #06b6d4 80%, #4FACFE 100%)`,
            backgroundSize: "220% 100%",
            border: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? alpha("#34d399", 0.55)
                : alpha("#10b981", 0.45),
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 12px 28px ${alpha("#10b981", 0.32)}, 0 0 30px ${alpha("#22c55e", 0.28)}, inset 0 1px 0 ${alpha("#fff", 0.22)}`
                : `0 10px 24px ${alpha("#10b981", 0.32)}, 0 0 24px ${alpha("#22c55e", 0.22)}, inset 0 1px 0 ${alpha("#fff", 0.5)}`,
            ...homeCtaGradientShiftKeyframes,
            ...homeCtaBounceKeyframes,
            animation: "homeCtaGradientShift 6s ease-in-out infinite, homeCtaBounce 2.4s ease-in-out infinite",
            transition: "transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease, border-color 0.25s ease",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              background: `linear-gradient(90deg, ${alpha("#fff", 0)} 0%, ${alpha("#fff", 0.55)} 50%, ${alpha("#fff", 0)} 100%)`,
              ...homeCtaShineKeyframes,
              animation: "homeCtaShine 2.8s ease-in-out infinite",
              pointerEvents: "none",
            },
            "&:hover": {
              transform: "translateY(-2px) scale(1.03)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 18px 38px ${alpha("#10b981", 0.42)}, 0 0 44px ${alpha("#22c55e", 0.4)}, inset 0 1px 0 ${alpha("#fff", 0.28)}`
                  : `0 14px 30px ${alpha("#10b981", 0.42)}, 0 0 34px ${alpha("#22c55e", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.6)}`,
              borderColor: alpha("#34d399", 0.85),
            },
            "&:active": {
              transform: "translateY(0) scale(0.99)",
            },
          })}
        >
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem" },
              lineHeight: 1,
              flexShrink: 0,
              filter: `drop-shadow(0 0 6px ${alpha("#fff", 0.6)})`,
              position: "relative",
              zIndex: 1,
            }}
            aria-hidden
          >
            🚀
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "#ffffff",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              textAlign: "center",
              letterSpacing: "0.03em",
              textShadow: `0 1px 2px ${alpha("#064e3b", 0.55)}, 0 0 12px ${alpha("#fff", 0.25)}`,
              position: "relative",
              zIndex: 1,
              whiteSpace: "nowrap",
            }}
          >
            Download our App
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "0.85rem", sm: "1rem" },
              lineHeight: 1,
              flexShrink: 0,
              color: "#ffffff",
              fontWeight: 900,
              position: "relative",
              zIndex: 1,
              textShadow: `0 1px 2px ${alpha("#064e3b", 0.55)}`,
            }}
            aria-hidden
          >
            →
          </Typography>
        </Paper>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {selfInfo ? (
        <Card
          sx={{
            mb: 2.5,
            borderRadius: 3.5,
            overflow: "hidden",
            position: "relative",
            border: "1px solid",
            borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.22 : 0.12),
            background: isDark
              ? `linear-gradient(165deg, ${alpha("#1a1f30", 0.97)} 0%, ${alpha("#12151f", 0.94)} 45%, ${alpha("#0c0e14", 0.98)} 100%)`
              : alpha(theme.palette.background.paper, 0.98),
            backdropFilter: "blur(16px)",
            boxShadow: isDark
              ? `0 12px 48px ${alpha("#000", 0.45)}, 0 0 0 1px ${alpha("#fff", 0.04)} inset, 0 -1px 0 ${alpha(portal.indigo, 0.12)} inset, 0 0 40px ${alpha(portal.cyan, 0.06)}`
              : `0 12px 40px ${alpha(theme.palette.primary.main, 0.07)}, 0 1px 0 ${alpha("#fff", 0.8)} inset`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 38%, ${portal.purple} 68%, ${portal.pink} 100%)`,
              opacity: isDark ? 0.92 : 0.85,
            },
            ...homeCardGradientBorderSx(
              theme,
              visualCardLoading.self,
              `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 38%, ${portal.purple} 68%, ${portal.pink} 100%)`,
              isDark
                ? `linear-gradient(165deg, ${alpha("#1a1f30", 0.97)} 0%, ${alpha("#12151f", 0.94)} 45%, ${alpha("#0c0e14", 0.98)} 100%)`
                : alpha(theme.palette.background.paper, 0.98)
            ),
          }}
        >
          <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, pt: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "center",
                  columnGap: { xs: 0.75, sm: 1 },
                  width: "100%",
                  py: 0.25,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <PersonIcon
                    sx={{
                      color: portal.amber,
                      fontSize: 28,
                      filter: isDark ? `drop-shadow(0 0 12px ${alpha(portal.amber, 0.55)})` : `drop-shadow(0 0 4px ${alpha(portal.amber, 0.4)})`,
                      ...homeIconFloatKeyframes,
                      animation: "homeIconFloat 3.4s ease-in-out infinite",
                    }}
                  />
                  <Typography
                    component={RouterLink}
                    to="/dashboard/ad-streak"
                    variant="h6"
                    title="Go to My Activity"
                    sx={{
                      ...portalGradientTextSx({ fontWeight: 800, letterSpacing: "0.06em" }),
                      fontSize: "1.05rem",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      textAlign: "left",
                      textDecoration: "none",
                      borderBottom: `2px solid ${alpha(portal.cyan, 0.45)}`,
                      textUnderlineOffset: "0.2em",
                      pb: 0.15,
                      cursor: "pointer",
                      transition: "opacity 0.2s, border-color 0.2s",
                      "&:hover": {
                        opacity: 0.92,
                        borderBottomColor: alpha(portal.cyan, 0.75),
                      },
                    }}
                  >
                    YOU
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  flexWrap="nowrap"
                  spacing={1}
                  sx={{
                    gap: { xs: 0.45, sm: 0.85 },
                    flexShrink: 0,
                    justifySelf: "end",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, isDark ? 0.35 : 0.25),
                      borderRadius: 999,
                      px: { xs: 0.55, sm: 1.1, md: 1.25 },
                      py: 0.45,
                      flexShrink: 0,
                      maxWidth: "100%",
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#152018", 0.95)} 0%, ${alpha("#0f1510", 0.98)} 100%)`
                        : alpha(theme.palette.success.main, 0.08),
                      boxShadow: isDark ? `0 0 20px ${alpha(theme.palette.success.main, 0.08)}` : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.light,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "right",
                        letterSpacing: "0.02em",
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        maxWidth: { xs: "min(9.5rem, 38vw)", md: `${selfStatsLayoutChars.rankChars}ch` },
                      }}
                    >
                      {selfInfo.rank}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay)
                        ? alpha(theme.palette.divider, isDark ? 0.35 : 0.2)
                        : alpha(theme.palette.warning.main, 0.45),
                      borderRadius: 999,
                      px: { xs: 0.55, sm: 1.1, md: 1.25 },
                      py: 0.45,
                      flexShrink: 0,
                      maxWidth: "100%",
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#1c1618", 0.95)} 0%, ${alpha("#141012", 0.98)} 100%)`
                        : alpha(theme.palette.background.paper, 0.95),
                      boxShadow: !isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay) && isDark
                        ? `0 0 18px ${alpha(theme.palette.warning.main, 0.12)}`
                        : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "common.white",
                        textAlign: "right",
                        letterSpacing: "0.03em",
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        "& > .home-streak-fire": {
                          ...homeStreakPulseKeyframes,
                          animation: "homeStreakPulse 1.8s ease-in-out infinite",
                          display: "inline-block",
                          transformOrigin: "center",
                        },
                      }}
                    >
                      <span>{selfInfo.currentStreak}</span>
                      <span className="home-streak-fire" aria-hidden>🔥</span>
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            <Box
              sx={{
                pt: 1,
                pb: 0.25,
                borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
                background: isDark
                  ? `linear-gradient(180deg, ${alpha(portal.indigo, 0.06)} 0%, transparent 100%)`
                  : alpha(portal.cyan, 0.03),
                borderRadius: 2,
                px: { xs: 0.5, sm: 0.75 },
                mx: { xs: -0.5, sm: -0.75 },
              }}
            >
              <SectionEyebrow
                sx={{
                  mb: 1.15,
                  pl: 0.25,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  ...portalGradientTextSx(
                    { fontWeight: 800, opacity: 1 },
                    `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`
                  ),
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: portal.gradientBar,
                    boxShadow: `0 0 10px ${alpha(portal.cyan, 0.65)}`,
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                Progress &amp; milestones
              </SectionEyebrow>
            <Stack spacing={1.25} sx={{ width: "100%" }}>
              <ThinLadderBar
                theme={theme}
                emoji="🎯"
                fillPercent={selfInfo.todayGoalPercent ?? 0}
                label={`Today's goal progress (${selfInfo.adsWatchedToday ?? 0}/${selfInfo.goalPerDay ?? 10})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="👤"
                fillPercent={selfInfo.referralsBarPercent ?? 0}
                label={`Direct referrals progress toward rank tiers (counts ${RANK_BAR_REFERRAL_THRESHOLDS.join(
                  ", "
                )})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="📶"
                fillPercent={selfInfo.levelsBarPercent ?? 0}
                label={`Network depth progress toward rank tiers (depth ${RANK_BAR_DEPTH_THRESHOLDS.join(", ")})`}
              />
            </Stack>
            </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card
        sx={{
          borderRadius: 3.5,
          overflow: "hidden",
          position: "relative",
          border: "1px solid",
          borderColor: alpha(isDark ? portal.pink : theme.palette.divider, isDark ? 0.24 : 0.12),
          background: isDark
            ? `linear-gradient(165deg, ${alpha("#1f1824", 0.96)} 0%, ${alpha("#14121a", 0.94)} 50%, ${alpha("#0c0b10", 0.98)} 100%)`
            : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: "blur(16px)",
          boxShadow: isDark
            ? `0 12px 48px ${alpha("#000", 0.4)}, inset 0 1px 0 ${alpha(portal.pink, 0.06)}, 0 0 36px ${alpha(portal.pink, 0.05)}`
            : `0 12px 36px ${alpha(theme.palette.primary.main, 0.06)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${portal.pink} 0%, ${portal.purple} 45%, ${portal.cyan} 100%)`,
            opacity: isDark ? 0.9 : 0.75,
          },
          ...homeCardGradientBorderSx(
            theme,
            visualCardLoading.referrals,
            `linear-gradient(90deg, ${portal.pink} 0%, ${portal.purple} 45%, ${portal.cyan} 100%)`,
            isDark
              ? `linear-gradient(165deg, ${alpha("#1f1824", 0.96)} 0%, ${alpha("#14121a", 0.94)} 50%, ${alpha("#0c0b10", 0.98)} 100%)`
              : alpha(theme.palette.background.paper, 0.98)
          ),
        }}
      >
        <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, pt: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{
              mb: 0.75,
              gap: 1,
              pb: 1.15,
              borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.1)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.15} flexWrap="wrap" useFlexGap>
              <GroupsIcon
                sx={{
                  color: portal.pink,
                  fontSize: 30,
                  filter: isDark ? `drop-shadow(0 0 14px ${alpha(portal.pink, 0.55)})` : `drop-shadow(0 0 4px ${alpha(portal.pink, 0.4)})`,
                  ...homeIconFloatKeyframes,
                  animation: "homeIconFloat 3.6s ease-in-out infinite",
                }}
              />
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  letterSpacing: "0.04em",
                  color: portal.pink,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                }}
              >
                My Referrals
              </Typography>
              <Chip
                size="small"
                label={referrals.length}
                sx={{
                  fontWeight: 700,
                  height: 26,
                  color: "common.white",
                  border: "1px solid",
                  borderColor: alpha(portal.cyan, 0.4),
                  bgcolor: alpha(portal.cyan, 0.14),
                  boxShadow: isDark ? `0 0 16px ${alpha(portal.cyan, 0.15)}` : "none",
                }}
              />
            </Stack>
            {referrals.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                {anyDownlineAdGoalOpen && (
                  <Tooltip title="Today's ad goal not completed yet (UTC day) for one or more downline members.">
                    <WarningAmberIcon sx={{ fontSize: 20, color: "warning.main" }} aria-hidden />
                  </Tooltip>
                )}
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "0.7rem",
                    color: anyDownlineAdGoalOpen ? "warning.light" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatUtcMidnightCountdown(utcMidnightRemainingMs)}
                </Typography>
              </Stack>
            )}
          </Stack>

          {referrals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No referrals found.
            </Typography>
          ) : (
            <Stack spacing={1.35}>
              {referrals.map((ref) => (
                  <Box
                    key={ref.userId}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, isDark ? 0.4 : 0.12),
                      borderRadius: 3,
                      p: { xs: 1.35, sm: 1.5 },
                      pl: { xs: 1.6, sm: 1.85 },
                      background: isDark
                        ? `linear-gradient(150deg, ${alpha("#1a1d28", 0.92)} 0%, ${alpha("#12141c", 0.95)} 100%)`
                        : alpha(theme.palette.background.paper, 0.97),
                      boxShadow: isDark
                        ? `inset 0 1px 0 ${alpha("#fff", 0.05)}, 0 4px 20px ${alpha("#000", 0.2)}`
                        : `0 2px 12px ${alpha(theme.palette.primary.main, 0.04)}`,
                      transition: "border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "10%",
                        bottom: "10%",
                        width: 4,
                        borderRadius: "0 4px 4px 0",
                        background: `linear-gradient(180deg, ${portal.cyan} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`,
                        opacity: 0.75,
                        boxShadow: isDark ? `0 0 12px ${alpha(portal.pink, 0.4)}` : "none",
                        transition: "opacity 0.25s ease, width 0.25s ease, box-shadow 0.25s ease",
                      },
                      ...homeCardGradientBorderSx(
                        theme,
                        visualCardLoading.referrals,
                        `linear-gradient(180deg, ${portal.cyan} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`,
                        isDark
                          ? `linear-gradient(150deg, ${alpha("#1a1d28", 0.92)} 0%, ${alpha("#12141c", 0.95)} 100%)`
                          : alpha(theme.palette.background.paper, 0.97)
                      ),
                      "&:hover": {
                        transform: "translateX(2px) translateY(-2px)",
                        borderColor: alpha(portal.pink, isDark ? 0.4 : 0.3),
                        boxShadow: isDark
                          ? `inset 0 1px 0 ${alpha("#fff", 0.06)}, 0 12px 32px ${alpha("#000", 0.4)}, 0 0 24px ${alpha(portal.pink, 0.15)}`
                          : `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 18px ${alpha(portal.pink, 0.12)}`,
                        "&::before": {
                          opacity: 1,
                          width: 5,
                          boxShadow: isDark ? `0 0 18px ${alpha(portal.pink, 0.7)}` : `0 0 12px ${alpha(portal.pink, 0.4)}`,
                        },
                      },
                    }}
                  >
                    <Stack spacing={1.1} sx={{ width: "100%", minWidth: 0 }}>
                      <ReferralCardNamePillsRow
                        referral={ref}
                        theme={theme}
                        isDark={isDark}
                        referralLayoutChars={referralLayoutChars}
                      />
                      <ThinLadderBar
                        theme={theme}
                        emoji="🎯"
                        fillPercent={ref.todayGoalPercent ?? 0}
                        label={`Referral today's goal progress (${ref.adsWatchedToday ?? 0}/${ref.goalPerDay ?? 10})`}
                      />
                      <ThinLadderBar
                        theme={theme}
                        emoji="👤"
                        fillPercent={ref.referralsBarPercent ?? 0}
                        label="Referral direct referrals progress toward rank tiers"
                      />
                      <ThinLadderBar
                        theme={theme}
                        emoji="📶"
                        fillPercent={ref.levelsBarPercent ?? 0}
                        label="Referral network depth progress toward rank tiers"
                      />
                    </Stack>
                  </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Centred "Link copied" toast — rendered through a Portal at
          document.body so no parent overflow/transform/z-index can hide it. */}
      {copySnack.open && typeof document !== "undefined"
        ? createPortal(
            <Box
              role="status"
              aria-live="polite"
              onClick={() =>
                setCopySnack((s) => ({ ...s, open: false }))
              }
              sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 2147483647,
                "@keyframes homeCopyToastPop": {
                  "0%": { opacity: 0, transform: "scale(0.85)" },
                  "60%": { opacity: 1, transform: "scale(1.04)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            >
              <Box
                sx={{
                  pointerEvents: "auto",
                  px: 3,
                  py: 1.75,
                  borderRadius: 999,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  textAlign: "center",
                  minWidth: 200,
                  maxWidth: "82vw",
                  background:
                    copySnack.severity === "success"
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                  boxShadow:
                    "0 20px 55px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset",
                  animation: "homeCopyToastPop 220ms ease-out",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {copySnack.severity === "success" ? "✅" : "⚠️"}{" "}
                {copySnack.message}
              </Box>
            </Box>,
            document.body
          )
        : null}
    </Box>
  );
}
